import { supabase } from '../lib/supabase';

export const LIVE_SESSION_TIPOS = {
  onboarding: { value: 'onboarding', label: 'Onboarding' },
  entrenamiento_general: { value: 'entrenamiento_general', label: 'Entrenamiento General' },
};

export const LIVE_SESSION_TIPO_OPTIONS = Object.values(LIVE_SESSION_TIPOS);

export const LIVE_SESSION_ESTADOS = {
  abierto: { value: 'abierto', label: 'Abierto', color: 'text-brand-green' },
  asignado: { value: 'asignado', label: 'Asignado', color: 'text-brand-cyan' },
  completado: { value: 'completado', label: 'Completado', color: 'text-[var(--text-secondary)]' },
};

function mapProfile(profile) {
  if (!profile) return null;
  return Array.isArray(profile) ? profile[0] : profile;
}

function resolveSlotZoom(row, zoomDefault) {
  const overrideUrl = row.zoom_url_override?.trim();
  if (overrideUrl) {
    return {
      zoomUrl: overrideUrl,
      zoomPasscode: row.zoom_passcode_override ?? '',
      zoomIsOverride: true,
    };
  }
  const zoom = mapProfile(zoomDefault);
  return {
    zoomUrl: zoom?.zoom_url ?? '',
    zoomPasscode: zoom?.zoom_passcode ?? '',
    zoomIsOverride: false,
  };
}

export function mapLiveSessionSlotRow(row) {
  const entrenador = mapProfile(row.entrenador);
  const reclamadoPor = mapProfile(row.reclamado_por_profile);
  const { zoomUrl, zoomPasscode, zoomIsOverride } = resolveSlotZoom(row, row.zoom);

  return {
    id: row.id,
    scheduledAt: row.scheduled_at,
    tipo: row.tipo,
    tipoLabel: LIVE_SESSION_TIPOS[row.tipo]?.label ?? row.tipo,
    tema: row.tema ?? '',
    estado: row.estado,
    estadoLabel: LIVE_SESSION_ESTADOS[row.estado]?.label ?? row.estado,
    entrenador: entrenador
      ? {
          id: entrenador.id,
          fullName: entrenador.full_name,
          email: entrenador.email,
          phone: entrenador.phone ?? '',
        }
      : null,
    reclamadoPor: reclamadoPor
      ? {
          id: reclamadoPor.id,
          fullName: reclamadoPor.full_name,
        }
      : null,
    reclamadoAt: row.reclamado_at,
    completadoAt: row.completado_at,
    zoomUrl,
    zoomPasscode,
    zoomIsOverride,
    createdAt: row.created_at,
  };
}

const SLOT_SELECT = `
  id,
  scheduled_at,
  tipo,
  tema,
  zoom_url_override,
  zoom_passcode_override,
  estado,
  reclamado_at,
  completado_at,
  created_at,
  entrenador:gfi_profiles!gfi_live_session_slots_entrenador_id_fkey (
    id,
    full_name,
    email,
    phone
  ),
  reclamado_por_profile:gfi_profiles!gfi_live_session_slots_reclamado_por_fkey (
    id,
    full_name
  )
`;

export async function fetchUpcomingLiveSessionSlots({ includePast = false } = {}) {
  let query = supabase
    .from('gfi_live_session_slots')
    .select(SLOT_SELECT)
    .order('scheduled_at', { ascending: true });

  if (!includePast) {
    query = query.gte('scheduled_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());
  }

  const { data: slots, error } = await query;
  if (error) throw error;

  const { data: zoomRows, error: zoomError } = await supabase
    .from('gfi_live_session_zoom')
    .select('tipo, zoom_url, zoom_passcode');

  if (zoomError) throw zoomError;

  const zoomByTipo = Object.fromEntries((zoomRows ?? []).map((z) => [z.tipo, z]));

  return (slots ?? []).map((row) =>
    mapLiveSessionSlotRow({ ...row, zoom: zoomByTipo[row.tipo] ?? null }),
  );
}

export async function fetchLiveSessionZoomConfig() {
  const { data, error } = await supabase
    .from('gfi_live_session_zoom')
    .select('tipo, zoom_url, zoom_passcode, updated_at')
    .order('tipo');

  if (error) throw error;
  return data ?? [];
}

export async function createLiveSessionSlot({ scheduledAt, tipo, tema }) {
  const { data, error } = await supabase.rpc('gfi_admin_create_live_session_slot', {
    p_scheduled_at: scheduledAt,
    p_tipo: tipo,
    p_tema: tema?.trim() || null,
  });

  if (error) throw error;
  return data;
}

export async function claimLiveSessionSlot(slotId, { tema, zoomUrl, zoomPasscode } = {}) {
  const { data, error } = await supabase.rpc('gfi_claim_live_session_slot', {
    p_slot_id: slotId,
    p_tema: tema?.trim() || null,
    p_zoom_url: zoomUrl?.trim() || null,
    p_zoom_passcode: zoomPasscode?.trim() || null,
  });

  if (error) throw error;
  return data;
}

export async function releaseLiveSessionSlot(slotId) {
  const { data, error } = await supabase.rpc('gfi_admin_release_live_session_slot', {
    p_slot_id: slotId,
  });

  if (error) throw error;
  return data;
}

export async function completeLiveSessionSlot(slotId) {
  const { data, error } = await supabase.rpc('gfi_admin_complete_live_session_slot', {
    p_slot_id: slotId,
  });

  if (error) throw error;
  return data;
}

export async function upsertLiveSessionZoom({ tipo, zoomUrl, zoomPasscode }) {
  const { data, error } = await supabase.rpc('gfi_admin_upsert_live_session_zoom', {
    p_tipo: tipo,
    p_zoom_url: zoomUrl,
    p_zoom_passcode: zoomPasscode ?? '',
  });

  if (error) throw error;
  return data;
}

export async function notifyAdminOfClaim(slotId) {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData?.session?.access_token;
  if (!token) throw new Error('Sesión requerida');

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const res = await fetch(`${supabaseUrl}/functions/v1/live-session-claim-notify`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ slotId }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || 'No se pudo enviar la notificación al admin');
  }
  return json;
}

export async function fetchCommunityActivitySummary() {
  const { data, error } = await supabase.rpc('gfi_community_activity_summary');
  if (error) throw error;
  return data ?? {
    total_points: 0,
    live_sessions_completed: 0,
    leads_captured: 0,
    faq_answers: 0,
  };
}
